import { autoinject, bindable, computedFrom, Container } from 'aurelia-framework';
import { json } from 'aurelia-fetch-client';
import { NodeModel } from "../../models/node-model";
import { RowValidationErrorModel } from '../../models/row-validation-error-model';
import { ApiService } from '../../utils/api-service';
import { IServiceStructureServiceImportVM, IStructureParser } from "./structure-parsers/structure-parser";
import { ResourcedNotificationService, NotificationType } from "../../components/notifications/notification-service";
import { max, remove } from "lodash";
import { IMeasureAM, MeasureService } from '../../features/measures/services/measure-service';
import { PerformanceIndicatorService } from '../../features/performance-indicators/performance-indicator-service';
import { DataItemOption, IServiceDataItemsBase } from '../../features/customers/reported-service-data-item-service';
import { IAttachedDataItemBase, ReportedServiceDataItemType } from '../../features/reported-service-data-items/reported-service-data-item-models';
import { IPerformanceIndicatorAM } from '../../features/performance-indicators/performance-indicator-models';
import { DefaultDataItem, DefaultServiceDataItemService } from '../../features/defaultMeasures/default-service-data-item-service';
import { ServiceStructureService } from '../../features/services/service-structure-service';

@autoinject
export class ServiceStructureImport {
	@bindable importUrl: string;
	@bindable refreshStructure: () => void;
	@bindable parser: IStructureParser<NodeModel[]>;
	@bindable serviceStructureId: string;

	showPreview: boolean = false;
	submitting: boolean = false;
	loading: boolean = false;
	errors: RowValidationErrorModel[] = [];
	rawData: string;

	availableDataItems: DataItemOption[] = [];
	serviceDataItems: IServiceDataItemsBase = {};

	importVM: IServiceStructureServiceImportVM[];
	customerIdsForStructure: (string | null)[] = [];
	saveDataItemsToCustomers: boolean = false;
	saveDataItemsToStructure: boolean = false;
	saveMetadata: boolean = false;

	private defaultScopeTypeGroupId = "6c428a2f-5c7b-4e71-a931-249df6bad25a";

	constructor(
		private http: ApiService, 
		private notificationService: ResourcedNotificationService, 
		private dataItemService: DefaultServiceDataItemService,
		private measureService: MeasureService, 
		private performanceIndicatorService: PerformanceIndicatorService,
		private serviceStructureService: ServiceStructureService) {}

	async attached() {
		this.loading = true;
		const availableMeasures = (await this.measureService.getMeasures()).map(
			(m: IMeasureAM) => {
				const item = new DataItemOption(m);
				item.type = ReportedServiceDataItemType.Measure;
				return item;
			}
		);
		
		const performanceIndicators = (await this.performanceIndicatorService.getPerformanceIndicators()).map(
			(pi: IPerformanceIndicatorAM) => {
				const item = new DataItemOption(pi);
				item.type = ReportedServiceDataItemType.PerformanceIndicator;
				return item;
			}
		);
		
		this.availableDataItems = [].concat(
			availableMeasures,
			performanceIndicators
		);

		const items = await this.dataItemService.getDefaultServiceDataItemsForServiceStructureWithCustomers(
			this.defaultScopeTypeGroupId,
			this.serviceStructureId
		);

		// Create a dictionary of serviceId -> dataItems
		this.serviceDataItems = Object.keys(items).reduce((item, key) => {
			item[key] = items[key].map(item => ({ serviceId: item.serviceId, dataItemName: item.dataItemName, sortOrder: item.sortOrder }));
			return item;
		}, {});

		// Add null element to the beginning of the array to represent saving data items to structure
		this.customerIdsForStructure = [null, ...await this.serviceStructureService.getServiceStructureCustomers(this.serviceStructureId)];

		this.loading = false;
	}

	@computedFrom("rawData")
	get structureData(): NodeModel[] {
		const flatdata = this.parser.parseRawInput(this.rawData)
		this.importVM = this.parser.parseRawInputToImportVM(this.rawData);
		return this.createHierarchy(flatdata);
	}

	@computedFrom("importVM")
	get newDataItemsCount(): number {
		return this.importVM?.reduce((acc, node) => acc + (node.defaultDataItems ? node.defaultDataItems.length : 0), 0);
	}

	@computedFrom("newDataItemsCount")
	get isSaveDataItemsDisabled(): boolean {
		return !this.newDataItemsCount || this.newDataItemsCount === 0;
	}

	@computedFrom("importVM")
	get metadataCount(): number {
		return this.importVM?.reduce((acc, node) => acc + getNodeMetadataCount(node), 0);

		function getNodeMetadataCount(node: IServiceStructureServiceImportVM): number {
			const fields = ['auraIdentifier', 'orpIdentifier', 'extraField1', 'extraField2'];
			return fields.reduce((count, field) => count + (node[field] ? 1 : 0), 0);
		}
	}
	
	@computedFrom("metadataCount")
	get isSaveMetadataDisabled(): boolean {
		return !this.metadataCount || this.metadataCount === 0;
	}

	createHierarchy(flatNodes: NodeModel[]): NodeModel[] {
		const hierarchy = [];

		const rootNodes = flatNodes.filter((root: NodeModel) => root.level === 0);

		rootNodes.forEach(root => {
			this.createChildNodes(root, flatNodes);
			hierarchy.push(root);
		});

		return hierarchy;
	}

	private createChildNodes(node: NodeModel, nodeCollection: NodeModel[]): void {
		const children = remove(nodeCollection, (children: NodeModel) => {
			return children.parent === node;
		});

		if (children && children.length > 0) {
			children.forEach(o => this.createChildNodes(o, nodeCollection));
		}

		children.forEach(o => node.children.push(o));
	}

	toggleShowPreview(): void {
		this.showPreview = !this.showPreview;
	}

	async submit() {
		this.submitting = true;
		this.errors = null;
		
		try {
			const response = await this.http.fetch(`${this.importUrl}?saveServiceMetadata=${this.saveMetadata}`, { method: "post", body: json(this.importVM) });
			if (response.status !== 200) {
				const errors = await response.json();
				this.notificationService.push("Label_ErrorInSubmit", NotificationType.Error);
				this.errors = RowValidationErrorModel.createFromValidationErrors(errors);
			} else {
				// Importing a service structure returns a list of DTOs of default reported data items to be created for the structure
				const dtos = await response.json() as IAttachedDataItemBase[];
				
				this.refreshStructure();
				this.notificationService.push("ViewMessage_SaveSucceeded", NotificationType.Success);
				this.rawData = "";
				this.showPreview = false;
				
				// Create default reported data items for the structure
				if (dtos.length > 0 && dtos[0].serviceStructureId && dtos.every(d => d.serviceStructureId === dtos[0].serviceStructureId)) {
					const { items, errors } = this.mapDataItems(dtos, this.customerIdsForStructure);

					const itemsToSave = items.filter(item => 
						(item.customerId === null && this.saveDataItemsToStructure) || 
						(item.customerId !== null && this.saveDataItemsToCustomers));
					
					if (itemsToSave.length > 0 ) {
						await this.http.fetch(`/api/service-structures/${dtos[0].serviceStructureId}/default-measures/for-scopetypegroup/${this.defaultScopeTypeGroupId}`, { method: "post", body: json(itemsToSave) });
						
						this.notificationService.push("StructureImport_SuccessMessage_DataItemsSaved", NotificationType.Success);
						if (errors.length > 0) {
							errors.forEach(e => this.notificationService.push("StructureImport_ErrorMessage_DataItemNotFound", NotificationType.Warning, null, [e.dataItemName]));
						} 
					}
				}
			}
		} catch (error) {
			this.submitting = false;
			this.notificationService.push("Label_ErrorInSubmit", NotificationType.Error, error);
			console.log("Unhandled error in request", error);
		} finally {
			this.submitting = false;
			this.showPreview = false;

			this.saveDataItemsToCustomers = false;
			this.saveDataItemsToStructure = false;
			this.saveMetadata = false;
		}
	}

	private mapDataItems(dtos: IAttachedDataItemBase[], customersForStructure: string[]): { items: DefaultDataItem[], errors: IAttachedDataItemBase[] } {
		const items: DefaultDataItem[] = [];
		const errors: IAttachedDataItemBase[] = [];
		
		for (const customerId of customersForStructure) {
			dtos.forEach(dto => { 
				
			const dataItem = this.availableDataItems.find(i => i.code === dto.dataItemName);

			// dataItemsExistsForService contains a dictionary of reported data items for each service in the structure.
			// The key is a combination of the serviceId and the customerId (if it exists).
			const key = customerId === null ? `structure_${dto.serviceId}` : `${customerId}_${dto.serviceId}`;
			const dataItemExists = this.dataItemExistsForService(key, dataItem?.name);
			
			if (dataItem && !dataItemExists) {
				const sortOrder = this.resolveSortOrder(key);
				items.push(new DefaultDataItem({
					serviceId: dto.serviceId,
					scopeTypeGroupId: this.defaultScopeTypeGroupId,
					sortOrder: sortOrder,
					dataItemId: dataItem.id,
					type: dataItem.type,
					customerId: customerId,
				}));
				
				if (this.serviceDataItems[key]) {
					this.serviceDataItems[key].push({ serviceId: dto.serviceId, dataItemName: dto.dataItemName, sortOrder });
				} else {
					this.serviceDataItems[key] = [{ serviceId: dto.serviceId, dataItemName: dto.dataItemName, sortOrder }];
				}
			} else {
				// Only push the error if the data item is not already added to the service. We also want to avoid duplicates in the error list.
				!dataItemExists && !errors.some(e => e.dataItemName == dto.dataItemName) && errors.push({
					serviceId: dto.serviceId,
					dataItemName: dto.dataItemName,
					serviceStructureId: dto.serviceStructureId,
					sortOrder: -1,
				});
			}
		});
		}

		return {
			items,
			errors
		};
	}

	private resolveSortOrder(key: string): number {
		const items = this.serviceDataItems[key];
		if (items?.length > 0) {
			return max(items.map(i => i.sortOrder)) + 1;
		}
		return 1;
	}

	private dataItemExistsForService(key: string, dataItemName: string): boolean {
		return this.serviceDataItems[key]?.find(i => i.dataItemName === dataItemName) !== undefined;
	}
}
